import { useEffect, useMemo, useState } from "react";
import type { AdminChannel } from "@swift/shared";
import { api } from "../../lib/api";
import { useStore } from "../../lib/store";

export default function ChannelsTab() {
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const [channels, setChannels] = useState<AdminChannel[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    try {
      const { channels } = await api.adminListChannels();
      setChannels(channels);
      setError(null);
    } catch {
      setError("Kanalen kunnen niet worden opgehaald.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const selected = useMemo(
    () => (channels && selectedId ? channels.find((c) => c.id === selectedId) ?? null : null),
    [channels, selectedId],
  );

  function patchLocal(id: number, patch: Partial<AdminChannel>) {
    setChannels((prev) =>
      prev ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c)) : prev,
    );
  }
  function removeLocal(id: number) {
    setChannels((prev) => (prev ? prev.filter((c) => c.id !== id) : prev));
    if (selectedId === id) setSelectedId(null);
  }

  if (channels === null) {
    return <div className="px-4 py-4 text-sm text-slate-400">Laden…</div>;
  }

  /* Twee panelen naast elkaar passen niet op een telefoon: de lijst is 14rem
     breed en houdt van het detail nog geen 100 pixels over. Onder `sm` tonen we
     er daarom een van de twee - lijst, of het kanaal dat je aantikte, met een
     terugknop erboven. Vanaf `sm` staan ze weer gewoon naast elkaar. */
  const detailOpen = creating || selected !== null;

  return (
    <div className="flex h-full">
      <aside
        className={`${detailOpen ? "hidden sm:flex" : "flex"} w-full shrink-0 flex-col border-r border-slate-700 sm:w-56`}
      >
        <div className="border-b border-slate-700 p-2">
          <button
            onClick={() => setCreating(true)}
            className="w-full rounded bg-accent px-2 py-2 text-xs font-medium text-white hover:bg-accent-hover sm:py-1"
          >
            + Nieuw kanaal
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto">
          {channels.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => {
                  setSelectedId(c.id);
                  setCreating(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                  selectedId === c.id ? "bg-slate-700 text-slate-100" : "text-slate-300 hover:bg-slate-700"
                }`}
              >
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-slate-500">#</span> {c.name}
                </span>
                {c.is_broadcast && (
                  <span
                    className="rounded-full bg-accent/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-accent"
                    title="Broadcast — iedereen wordt automatisch lid"
                  >
                    bc
                  </span>
                )}
                {c.is_announcement && (
                  <span
                    className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-300"
                    title="Aankondigingskanaal — alleen admins posten"
                  >
                    📢
                  </span>
                )}
                <span className="text-[10px] text-slate-500">{c.member_ids.length}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div
        className={`${detailOpen ? "block" : "hidden sm:block"} min-w-0 flex-1 overflow-y-auto p-4`}
      >
        {detailOpen && (
          <button
            onClick={() => {
              setSelectedId(null);
              setCreating(false);
            }}
            className="mb-3 -ml-1 flex items-center gap-1 rounded px-1 py-1 text-sm text-slate-400 hover:text-slate-200 sm:hidden"
          >
            ← Kanalen
          </button>
        )}
        {error && (
          <div className="mb-3 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-200">
            {error}
          </div>
        )}
        {creating ? (
          <CreateChannelForm
            users={users}
            onCancel={() => setCreating(false)}
            onCreated={(ch) => {
              setChannels((prev) => (prev ? [ch, ...prev] : [ch]));
              setSelectedId(ch.id);
              setCreating(false);
            }}
          />
        ) : selected ? (
          <ChannelDetail
            channel={selected}
            users={users}
            currentUserId={me?.id ?? null}
            onPatch={(patch) => patchLocal(selected.id, patch)}
            onDeleted={() => removeLocal(selected.id)}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Selecteer links een kanaal of maak een nieuwe aan.
          </div>
        )}
      </div>
    </div>
  );
}

function CreateChannelForm({
  users,
  onCancel,
  onCreated,
}: {
  users: Record<number, { id: number; username: string; display_name: string }>;
  onCancel(): void;
  onCreated(ch: AdminChannel): void;
}) {
  const [name, setName] = useState("");
  const [broadcast, setBroadcast] = useState(false);
  const [announcement, setAnnouncement] = useState(false);
  const [privateCh, setPrivateCh] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setError("Geef een naam op.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { conversation } = await api.adminCreateChannel({
        name: name.trim(),
        member_ids: [...picked],
        broadcast,
        is_announcement: announcement,
        is_private: privateCh,
      });
      // De server geeft een Conversation terug; converteer naar
      // AdminChannel-vorm voor lokale state.
      const ch: AdminChannel = {
        id: conversation.id,
        name: conversation.name ?? name.trim(),
        is_broadcast: broadcast,
        is_announcement: announcement,
        is_private: privateCh,
        retention_days: null,
        member_ids: conversation.member_ids,
        last_message_at: conversation.last_message_at,
      };
      onCreated(ch);
    } catch {
      setError("Aanmaken mislukt.");
    } finally {
      setBusy(false);
    }
  }

  const sortedUsers = Object.values(users).sort((a, b) =>
    a.display_name.localeCompare(b.display_name),
  );

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-slate-200">Nieuw kanaal</h2>
      {error && <div className="text-xs text-red-400">{error}</div>}
      <label className="block">
        <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">Naam</div>
        <input
          autoFocus
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-200 outline-none focus:border-accent"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          placeholder="bv. Directie"
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={broadcast}
          onChange={(e) => setBroadcast(e.target.checked)}
        />
        Broadcast — iedereen wordt automatisch lid (ook nieuwe collega's)
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={announcement}
          onChange={(e) => setAnnouncement(e.target.checked)}
        />
        Aankondiging — alleen admins mogen posten (iedereen leest + reageert)
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={privateCh}
          onChange={(e) => setPrivateCh(e.target.checked)}
        />
        Privé — niet-leden (ook andere admins) zien dit kanaal niet
      </label>
      {!broadcast && (
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">Initiële leden</div>
          <div className="max-h-48 overflow-y-auto rounded border border-slate-700">
            {sortedUsers.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-2 border-b border-slate-700 px-2 py-1 text-sm text-slate-300 last:border-b-0 hover:bg-slate-700"
              >
                <input
                  type="checkbox"
                  checked={picked.has(u.id)}
                  onChange={(e) => {
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(u.id);
                      else next.delete(u.id);
                      return next;
                    });
                  }}
                />
                {u.display_name}
                <span className="text-xs text-slate-500">@{u.username}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void submit()}
          disabled={busy}
          className="rounded bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? "…" : "Aanmaken"}
        </button>
        <button
          onClick={onCancel}
          className="rounded bg-slate-700 px-3 py-1 text-sm text-slate-200 hover:bg-slate-600"
        >
          Annuleer
        </button>
      </div>
    </div>
  );
}

function ChannelDetail({
  channel,
  users,
  currentUserId,
  onPatch,
  onDeleted,
}: {
  channel: AdminChannel;
  users: Record<number, { id: number; username: string; display_name: string }>;
  currentUserId: number | null;
  onPatch(patch: Partial<AdminChannel>): void;
  onDeleted(): void;
}) {
  const [nameDraft, setNameDraft] = useState(channel.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reset naam-draft bij wisselen van kanaal.
  useEffect(() => {
    setNameDraft(channel.name);
    setConfirmingDelete(false);
    setError(null);
  }, [channel.id, channel.name]);

  async function saveName() {
    const next = nameDraft.trim();
    if (!next || next === channel.name) return;
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateChannel(channel.id, { name: next });
      onPatch({ name: next });
    } catch {
      setError("Hernoemen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleBroadcast() {
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateChannel(channel.id, { is_broadcast: !channel.is_broadcast });
      onPatch({ is_broadcast: !channel.is_broadcast });
    } catch {
      setError("Broadcast-vlag aanpassen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleAnnouncement() {
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateChannel(channel.id, { is_announcement: !channel.is_announcement });
      onPatch({ is_announcement: !channel.is_announcement });
    } catch {
      setError("Aankondiging-vlag aanpassen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function togglePrivate() {
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateChannel(channel.id, { is_private: !channel.is_private });
      onPatch({ is_private: !channel.is_private });
    } catch {
      setError("Privé-vlag aanpassen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function saveRetention(days: number | null) {
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateChannel(channel.id, { retention_days: days });
      onPatch({ retention_days: days });
    } catch {
      setError("Bewaartermijn aanpassen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(userId: number) {
    setBusy(true);
    setError(null);
    try {
      await api.adminRemoveMember(channel.id, userId);
      onPatch({ member_ids: channel.member_ids.filter((id) => id !== userId) });
    } catch {
      setError("Lid verwijderen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function addMember(userId: number) {
    setBusy(true);
    setError(null);
    try {
      await api.adminAddMembers(channel.id, { user_ids: [userId] });
      onPatch({ member_ids: [...channel.member_ids, userId] });
    } catch {
      setError("Lid toevoegen mislukt.");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setError(null);
    try {
      await api.adminDeleteChannel(channel.id);
      onDeleted();
    } catch {
      setError("Verwijderen mislukt.");
      setBusy(false);
    }
  }

  const memberSet = new Set(channel.member_ids);
  const sortedMembers = channel.member_ids
    .map((id) => users[id])
    .filter((u): u is NonNullable<typeof u> => !!u)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  const candidates = Object.values(users)
    .filter((u) => !memberSet.has(u.id))
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  return (
    <div className="space-y-4">
      {error && <div className="text-xs text-red-400">{error}</div>}

      <div>
        <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">Naam</div>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-200 outline-none focus:border-accent"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            maxLength={64}
            onKeyDown={(e) => {
              if (e.key === "Enter") void saveName();
            }}
          />
          <button
            onClick={() => void saveName()}
            disabled={busy || nameDraft.trim() === channel.name || !nameDraft.trim()}
            className="rounded bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            Opslaan
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={channel.is_broadcast}
          disabled={busy}
          onChange={toggleBroadcast}
        />
        Broadcast — iedereen lid (ook nieuwe collega's)
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={channel.is_announcement}
          disabled={busy}
          onChange={toggleAnnouncement}
        />
        Aankondiging — alleen admins posten (iedereen leest + reageert)
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={channel.is_private}
          disabled={busy}
          onChange={togglePrivate}
        />
        Privé — niet-leden zien dit kanaal niet
      </label>
      <RetentionInput
        currentDays={channel.retention_days}
        busy={busy}
        onSave={saveRetention}
      />

      <div>
        <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">
          Leden ({sortedMembers.length})
        </div>
        <ul className="divide-y divide-slate-700 rounded border border-slate-700">
          {sortedMembers.map((u) => (
            <li key={u.id} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-300">
              <span className="min-w-0 flex-1 truncate">
                {u.display_name}
                <span className="ml-1 text-xs text-slate-500">@{u.username}</span>
                {u.id === currentUserId && (
                  <span className="ml-2 text-[10px] uppercase text-slate-500">jij</span>
                )}
              </span>
              <button
                onClick={() => void removeMember(u.id)}
                disabled={busy}
                className="rounded p-1 text-slate-500 hover:bg-red-900/40 hover:text-red-300 disabled:opacity-50"
                title="Lid verwijderen"
                aria-label="Lid verwijderen"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
        {candidates.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <select
              className="flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-200 outline-none"
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (Number.isInteger(id)) void addMember(id);
                e.target.value = "";
              }}
              disabled={busy}
            >
              <option value="">+ Lid toevoegen…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} (@{u.username})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="border-t border-slate-700 pt-3">
        {confirmingDelete ? (
          <div className="flex items-center gap-2 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs">
            <span className="text-red-200">
              Kanaal #{channel.name} en alle berichten + bijlages verwijderen?
            </span>
            <button
              onClick={() => void doDelete()}
              disabled={busy}
              className="rounded bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-500 disabled:opacity-50"
            >
              Ja, verwijderen
            </button>
            <button
              onClick={() => setConfirmingDelete(false)}
              className="rounded bg-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-600"
            >
              Annuleer
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmingDelete(true)}
            disabled={busy}
            className="rounded border border-red-800 bg-red-950/30 px-3 py-1 text-xs text-red-300 hover:bg-red-900/40"
          >
            Kanaal verwijderen
          </button>
        )}
      </div>
    </div>
  );
}

/** Inline input voor het retention_days-veld. Lege waarde = onbeperkt
 *  (null). Pure UI-component; bovenliggende edit-modal regelt de
 *  busy-state en de API-call via saveRetention. */
function RetentionInput({
  currentDays,
  busy,
  onSave,
}: {
  currentDays: number | null;
  busy: boolean;
  onSave(days: number | null): void | Promise<void>;
}) {
  const [draft, setDraft] = useState(currentDays === null ? "" : String(currentDays));
  useEffect(() => {
    setDraft(currentDays === null ? "" : String(currentDays));
  }, [currentDays]);
  const valid =
    draft.trim() === "" ||
    (/^\d+$/.test(draft.trim()) && Number(draft) > 0);

  function commit() {
    if (!valid) return;
    const t = draft.trim();
    const parsed = t === "" ? null : Number(t);
    if (parsed === currentDays) return;
    void onSave(parsed);
  }

  return (
    <div className="flex items-center gap-2 text-sm text-slate-300">
      <span className="shrink-0">🕒 Berichten bewaren:</span>
      <input
        type="number"
        min={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        disabled={busy}
        placeholder="onbeperkt"
        className={`w-24 rounded border bg-slate-800 px-2 py-1 text-sm outline-none ${
          valid ? "border-slate-700 focus:border-accent" : "border-red-500"
        }`}
      />
      <span className="text-xs text-slate-500">dagen (leeg = onbeperkt)</span>
    </div>
  );
}
